import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Search, IndianRupee, MessageCircle } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function AdminKhata() {
  const [data, setData] = useState({ customers: [], total_due: 0 });
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [payOpen, setPayOpen] = useState(null);
  const [amount, setAmount] = useState("");
  const [firm, setFirm] = useState("Rani Super Market");
  const [minDue, setMinDue] = useState(500);
  const [bulking, setBulking] = useState(false);

  const load = () => api.get("/admin/khata").then((r) => setData(r.data));
  useEffect(() => { load(); api.get("/admin/business-settings").then((r) => setFirm(r.data.name || "Rani Super Market")).catch(() => {}); }, []);

  const remind = async (c) => {
    const digits = (c.phone || "").replace(/\D/g, "");
    if (digits.length < 10) return toast.error("Valid phone nahi hai");
    try { await api.post("/admin/khata/remind", { phone: c.phone, name: c.name, due: c.due }); } catch (e) { console.warn("reminder log failed", e); }
    const msg = `Namaste ${c.name || ""}! ${firm} — aapka udhaar (baaki) ₹${Number(c.due).toLocaleString("en-IN")} hai. Kripya jaldi jama kar dein. Dhanyavaad!`;
    window.open(`https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`, "_blank");
    load();
  };

  const remindAll = async () => {
    setBulking(true);
    try { const { data } = await api.post("/admin/khata/remind-all", { min_due: Number(minDue) || 0 }); toast.success(`${data.sent} customer(s) ko reminder bheja (₹${minDue}+)`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBulking(false); }
  };

  const openDetail = async (c) => { const { data } = await api.get(`/admin/khata/${encodeURIComponent(c.phone)}`); setDetail(data); };

  const collect = async () => {
    try {
      await api.post("/admin/khata/pay", { phone: payOpen.phone, name: payOpen.name, amount: Number(amount) });
      toast.success("Payment recorded"); setPayOpen(null); setAmount(""); load(); if (detail) openDetail(payOpen);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rows = data.customers.filter((c) => c.due > 0.001).filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));

  return (
    <div className="space-y-5" data-testid="admin-khata">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><Wallet className="text-emerald-600" /><h1 className="text-2xl font-bold">Khata / Udhaar</h1></div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1">
            <span className="text-xs text-slate-400 pl-1">₹</span>
            <input data-testid="khata-mindue" type="number" value={minDue} onChange={(e) => setMinDue(e.target.value)} className="w-16 text-sm outline-none" />
            <button data-testid="khata-remind-all" onClick={remindAll} disabled={bulking} className="text-xs font-bold px-2.5 py-1.5 bg-green-600 text-white rounded-lg inline-flex items-center gap-1 disabled:opacity-50"><MessageCircle size={12} /> {bulking ? "…" : "Remind All"}</button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-sm font-bold text-amber-800" data-testid="khata-total-due">Total outstanding: {inr(data.total_due)}</div>
        </div>
      </div>
      <div className="relative max-w-xs"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone…" className="pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-lg text-sm w-full" /></div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50"><th className="text-left py-3 px-4">Customer</th><th className="text-left py-3 px-4">Phone</th><th className="text-right py-3 px-4">Due</th><th className="text-right py-3 px-4">Actions</th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.phone} data-testid={`khata-row-${c.phone}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="py-2.5 px-4 font-semibold">{c.name || "Customer"}</td>
                <td className="py-2.5 px-4 text-slate-500">{c.phone}</td>
                <td className="py-2.5 px-4 text-right font-bold text-amber-700">{inr(c.due)}{c.last_reminded && <span className="block text-[10px] font-medium text-slate-400" data-testid={`khata-lastremind-${c.phone}`}>Reminded {new Date(c.last_reminded).toLocaleDateString("en-IN")}</span>}</td>
                <td className="py-2.5 px-4 text-right space-x-2">
                  <button onClick={() => openDetail(c)} className="text-xs font-bold text-slate-600 hover:text-slate-900">Ledger</button>
                  <button data-testid={`khata-remind-${c.phone}`} onClick={() => remind(c)} className="text-xs font-bold px-2.5 py-1 bg-green-600 text-white rounded-lg inline-flex items-center gap-1"><MessageCircle size={12} /> Remind</button>
                  <button data-testid={`khata-collect-${c.phone}`} onClick={() => { setPayOpen(c); setAmount(c.due); }} className="text-xs font-bold px-2.5 py-1 bg-emerald-600 text-white rounded-lg">Collect</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="4" className="text-center py-10 text-slate-400">Koi udhaar baaki nahi 🎉</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader><DialogTitle>Collect payment · {payOpen?.name || payOpen?.phone}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Outstanding: <b className="text-amber-700">{inr(payOpen?.due || 0)}</b></p>
          <div className="flex items-center gap-2 mt-2"><IndianRupee size={16} className="text-slate-400" /><input data-testid="khata-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
          <button data-testid="khata-pay-confirm" onClick={collect} className="mt-3 w-full py-2.5 bg-emerald-600 text-white rounded-lg font-bold">Record Payment</button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.name || detail?.phone} · Due {inr(detail?.due || 0)}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(detail?.entries || []).map((e) => (
              <div key={e.id} className="flex justify-between text-sm border-b border-slate-50 pb-1.5">
                <div><p className="font-semibold">{e.type === "debit" ? "Credit sale" : "Payment"} {e.invoice_no ? `· ${e.invoice_no}` : ""}</p><p className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</p></div>
                <div className="text-right"><span className={`font-bold ${e.type === "debit" ? "text-amber-700" : "text-emerald-600"}`}>{e.type === "debit" ? "+" : "-"}{inr(e.amount)}</span><p className="text-xs text-slate-400">bal {inr(e.balance_after)}</p></div>
              </div>
            ))}
          </div>
          {(detail?.reminders || []).length > 0 && (
            <div className="mt-4" data-testid="khata-reminder-history">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Reminder history ({detail.reminders.length})</p>
              <div className="space-y-1.5">
                {detail.reminders.map((r) => (
                  <div key={r.id} className="flex justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                    <span className="inline-flex items-center gap-1 text-slate-600"><MessageCircle size={11} className="text-green-600" /> {r.channel === "bulk" ? "Bulk reminder" : "WhatsApp reminder"} · ₹{Number(r.due).toLocaleString("en-IN")}</span>
                    <span className="text-slate-400">{new Date(r.created_at).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
